import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiOptions {
  immediate?: boolean;
  showError?: boolean;
}

export const useApi = <T>(
  apiFunction: () => Promise<T>,
  options: UseApiOptions = {}
) => {
  const { immediate = true, showError = true } = options;
  
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: immediate,
    error: null,
  });

  // Use ref to store the latest apiFunction
  const apiFunctionRef = useRef(apiFunction);
  apiFunctionRef.current = apiFunction;

  const execute = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await apiFunctionRef.current();
      setState({ data: result, loading: false, error: null });
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      setState({ data: null, loading: false, error: errorMessage });
      
      if (showError) {
        message.error(errorMessage);
      }
      
      throw error;
    }
  }, [showError]);

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [immediate, execute]);

  const refresh = useCallback(() => {
    return execute();
  }, [execute]);

  return {
    ...state,
    execute,
    refresh,
  };
};

export const useAsyncOperation = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async <T>(
    operation: () => Promise<T>,
    options: { 
      successMessage?: string; 
      errorMessage?: string;
      showSuccess?: boolean;
      showError?: boolean;
    } = {}
  ): Promise<T | null> => {
    const { 
      successMessage, 
      errorMessage, 
      showSuccess = true, 
      showError = true 
    } = options;

    setLoading(true);
    
    try {
      const result = await operation();
      
      if (showSuccess && successMessage) {
        message.success(successMessage);
      }
      
      return result;
    } catch (error: any) {
      const msg = errorMessage || error.response?.data?.message || error.message || 'An error occurred';
      
      if (showError) {
        message.error(msg);
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, execute };
};






